/**
 * DeleteConfirmDialog - Wiederverwendbare Lösch-Bestätigungs-Komponente
 * 
 * Verwendet exakt den gleichen UI-Flow wie in der Automatenübersicht:
 * - shadcn Dialog (kein window.confirm)
 * - Optionaler Löschgrund
 * - Klare Bestätigungs-Buttons
 * 
 * Props:
 * - open: boolean - Dialog offen/geschlossen
 * - onOpenChange: (open: boolean) => void - Handler für Dialog-State
 * - title: string - Dialog-Titel
 * - description: string - Beschreibungstext
 * - itemName: string - Name des zu löschenden Elements (für Anzeige)
 * - onConfirm: () => Promise<void> - Async Handler für Löschvorgang
 * - requireReason: boolean - Ob ein Löschgrund erforderlich ist (default: false)
 * - reasonMinLength: number - Mindestlänge für Löschgrund (default: 5)
 * - loading: boolean - Ladezustand während Löschvorgang
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Trash2, Loader2 } from 'lucide-react';

const DeleteConfirmDialog = ({
  open,
  onOpenChange,
  title = 'Löschen bestätigen',
  description,
  itemName,
  onConfirm,
  requireReason = false,
  reasonMinLength = 5,
  loading = false
}) => {
  const [reason, setReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (requireReason && (!reason || reason.trim().length < reasonMinLength)) {
      toast.error(`Löschgrund muss mindestens ${reasonMinLength} Zeichen lang sein`);
      return;
    }

    setIsDeleting(true);
    try {
      await onConfirm(reason);
      setReason('');
      onOpenChange(false);
    } catch (error) {
      // Error handling in onConfirm
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setReason('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      if (!newOpen) {
        setReason('');
      }
      onOpenChange(newOpen);
    }}>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 size={20} />
            {title}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleConfirm} className="space-y-4">
          <p className="text-sm text-slate-600">
            {description || (
              <>
                Bist du sicher, dass du {itemName ? <strong>&quot;{itemName}&quot;</strong> : 'diesen Eintrag'} löschen möchtest?
              </>
            )}
          </p>
          
          {requireReason && (
            <div>
              <Label>Löschgrund (mindestens {reasonMinLength} Zeichen)</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                minLength={reasonMinLength}
                rows={3}
                placeholder="Grund für das Löschen..."
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          
          <DialogFooter className="flex gap-3 sm:gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="flex-1"
              disabled={isDeleting || loading}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              variant="destructive"
              className="flex-1"
              disabled={isDeleting || loading}
            >
              {(isDeleting || loading) ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Löschen...
                </>
              ) : (
                'Löschen'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * DeleteButton - Trash Icon Button mit integriertem Delete Dialog
 * 
 * Einfachere Variante für schnelle Integration.
 * Rendert einen roten Trash-Button, der bei Klick den Dialog öffnet.
 * 
 * Props:
 * - onDelete: () => Promise<void> - Async Delete Handler
 * - title: string - Dialog Titel
 * - itemName: string - Name des Elements
 * - description: string - Optionale Beschreibung
 * - requireReason: boolean - Ob Löschgrund erforderlich
 * - disabled: boolean - Button deaktiviert
 * - size: 'sm' | 'default' - Button Größe
 */
export const DeleteButton = ({
  onDelete,
  title = 'Löschen bestätigen',
  itemName,
  description,
  requireReason = false,
  disabled = false,
  size = 'sm',
  className = ''
}) => {
  const [showDialog, setShowDialog] = useState(false);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDialog(true);
  };

  return (
    <>
      <Button
        variant="ghost"
        size={size}
        onClick={handleClick}
        disabled={disabled}
        className={`text-red-600 hover:text-red-700 hover:bg-red-50 ${className}`}
        type="button"
      >
        <Trash2 size={size === 'sm' ? 14 : 16} />
      </Button>
      
      <DeleteConfirmDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        title={title}
        itemName={itemName}
        description={description}
        onConfirm={onDelete}
        requireReason={requireReason}
      />
    </>
  );
};

export default DeleteConfirmDialog;
